import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { listDownloads, deleteDownload, redownloadItem, UserDownload, getDownloadTypeLabel, DownloadMode, DownloadType } from '@/services/downloadHistoryService';
import { FileDown, Loader2, Trash2, Clock, FileText, ClipboardCheck, Target, Layers, Presentation, LayoutGrid, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';

const TYPE_ICONS: Record<string, typeof FileText> = {
  document: FileText,
  test_pdf: ClipboardCheck,
  case_pdf: Target,
  flashcards_pdf: Layers,
  resumen: FileText,
  slides: Presentation,
  infografia: LayoutGrid,
  chat_export: MessageSquare,
};

interface DownloadsPanelProps {
  mode: DownloadMode;
}

interface TabConfig {
  value: string;
  label: string;
  types: DownloadType[];
}

function getTabsForMode(mode: DownloadMode): TabConfig[] {
  if (mode === 'funcionarios') {
    return [
      { value: 'all', label: 'Todo', types: [] },
      { value: 'documents', label: 'Documentos', types: ['document'] },
      { value: 'chat_exports', label: 'Chats', types: ['chat_export'] },
    ];
  }
  // oposiciones / oposiciones_c1
  return [
    { value: 'all', label: 'Todo', types: [] },
    { value: 'presentations', label: 'Presentaciones', types: ['slides'] },
    { value: 'infographics', label: 'Infografías', types: ['infografia'] },
    { value: 'resumen', label: 'Resúmenes', types: ['resumen'] },
    { value: 'tests', label: 'Tests', types: ['test_pdf'] },
    { value: 'cases', label: 'Casos', types: ['case_pdf'] },
    { value: 'chat_exports', label: 'Chats', types: ['chat_export'] },
  ];
}

function DownloadItem({ dl, onDelete, onRedownload }: { dl: UserDownload; onDelete: (id: string) => void; onRedownload: (dl: UserDownload) => void }) {
  const Icon = TYPE_ICONS[dl.download_type] || FileText;
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-md hover:bg-accent/50 transition-colors border border-transparent hover:border-border">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{dl.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full bg-secondary text-secondary-foreground font-bold">
              {getDownloadTypeLabel(dl.download_type as DownloadType)}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {new Date(dl.created_at).toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button size="sm" variant="outline" className="h-7 px-2" title="Descargar de nuevo" onClick={() => onRedownload(dl)}>
          <FileDown className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-muted-foreground hover:text-destructive" onClick={() => onDelete(dl.id)}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export function DownloadsPanel({ mode }: DownloadsPanelProps) {
  const { user } = useAuth();
  const [downloads, setDownloads] = useState<UserDownload[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const tabs = useMemo(() => getTabsForMode(mode), [mode]);
  const [activeTab, setActiveTab] = useState('all');

  const fetchDownloads = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const data = await listDownloads(user.id, mode);
    setDownloads(data);
    setLoading(false);
  }, [user, mode]);

  useEffect(() => {
    void fetchDownloads();
    const handleDownloadSaved = () => void fetchDownloads();
    window.addEventListener('munilex:download-saved', handleDownloadSaved);
    return () => window.removeEventListener('munilex:download-saved', handleDownloadSaved);
  }, [fetchDownloads]);

  const handleDelete = async (id: string) => {
    try {
      await deleteDownload(id);
      setDownloads(prev => prev.filter(d => d.id !== id));
      toast({ title: 'Descarga eliminada' });
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const handleRedownload = async (dl: UserDownload) => {
    try {
      const success = await redownloadItem(dl);
      if (!success) {
        toast({ title: 'Contenido no disponible', description: 'No se pudo recuperar el archivo original.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error al descargar', variant: 'destructive' });
    }
  };

  const filteredDownloads = useMemo(() => {
    const tab = tabs.find(t => t.value === activeTab);
    if (!tab || tab.types.length === 0) return downloads;
    return downloads.filter(d => tab.types.includes(d.download_type as DownloadType));
  }, [downloads, activeTab, tabs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (downloads.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No tienes descargas recientes.
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-hidden flex flex-col">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
        <TabsList className="flex flex-wrap h-auto gap-0.5 p-1 shrink-0">
          {tabs.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} className="text-xs px-2 py-1">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredDownloads.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">Sin descargas en esta categoría.</p>
          ) : (
            filteredDownloads.map(dl => (
              <DownloadItem key={dl.id} dl={dl} onDelete={handleDelete} onRedownload={handleRedownload} />
            ))
          )}
        </div>
      </Tabs>
    </div>
  );
}
